import { defineStore } from 'pinia'

import { useApiStore } from '@/stores/api'
import { useAuthenticationStore } from '@/stores/authentication'

import { hotReloadStore } from '@/utils/build'

import type { ProvisionDeviceResponse_Totp } from '@policyfly/protobuf/patrick'

export interface State {
  id: number | null
  email: string
  token: string
  password: string
  number: string
  firstName: string
}

export const useOnboardingStore = defineStore('onboarding', {
  state: (): State => ({
    id: null,
    email: '',
    token: '',
    password: '',
    number: '',
    firstName: '',
  }),

  getters: {
    loaded: (state) => !!state.firstName,
  },

  actions: {
    async loadUser ({ email, token }: { email: string, token: string }): Promise<void> {
      const apiStore = useApiStore()
      const response = await apiStore.user.validateInvitedUser({
        email,
        token,
      })
      this.id = response.legacyId ?? null
      this.firstName = response.firstName
      this.email = response.email
      this.token = token
    },
    async setupAccount (payload: { password: string }): Promise<void> {
      const apiStore = useApiStore()
      const { legacyId, bingoId } = await apiStore.user.setupAccount({
        password: payload.password,
        email: this.email,
        token: this.token,
      })
      this.token = ''
      const authenticationStore = useAuthenticationStore()
      authenticationStore.legacyId = legacyId ?? null
      authenticationStore.bingoId = bingoId ?? null
    },
    async provisionSMS (newNumber: string): Promise<void> {
      const apiStore = useApiStore()
      if (newNumber) this.number = newNumber
      const authenticationStore = useAuthenticationStore()
      await apiStore.user.provisionDevice({
        emailAddress: this.email,
        method: { oneofKind: 'phone', phone: { phoneNumber: this.number } },
        bingoId: authenticationStore.bingoId ?? undefined,
      })
    },
    async provisionQR (): Promise<ProvisionDeviceResponse_Totp> {
      const apiStore = useApiStore()
      const authenticationStore = useAuthenticationStore()
      const response = await apiStore.user.provisionDevice({
        emailAddress: this.email,
        method: { oneofKind: 'totp', totp: {} },
        bingoId: authenticationStore.bingoId ?? undefined,
      })
      if (response.method.oneofKind !== 'totp') {
        throw new Error('Unexpected response from server')
      }
      return response.method.totp
    },
    async provisionEmail (): Promise<void> {
      const apiStore = useApiStore()
      const authenticationStore = useAuthenticationStore()
      await apiStore.user.provisionDevice({
        emailAddress: this.email,
        method: { oneofKind: 'email', email: {} },
        bingoId: authenticationStore.bingoId ?? undefined,
      })
    },
  },
})

hotReloadStore(useOnboardingStore)
